import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Form, Stack, FormLabel, InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { parseNumericInput, HourValidationRules, MinuteValidationRules } from "../utils/InputValidator";
import { faCircleExclamation, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import { format, isValid } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { UserContext } from "..";
import { StringIsNullUndefinedOrEmpty } from "../utils/StringUtils";
import moment from "moment-timezone";
import { getPortLocalDatetimeNoLabel, getTimezone, isValidTZLocationOfPort } from "../helpers/TimezoneHelper";

/*date passed in should be local time - js seesm to automatically convert utc saved dates into local time*/
const DateTimePicker = (props) => {
  const [localDate, setLocalDate] = useState(null);
  const [localHour, setLocalHour] = useState("");
  const [localMinute, setLocalMinute] = useState("");
  const datepickerRef = useRef(null);

  const [timezone, setTimezone] = useState(null);

  // The 'init' guard in this component allows the validation to fail in the parent but the internal state of the component
  // is not overriden by the parent passing null back as the datetime prop
  const [inited, setInited] = useState(false);

  const userCtx = useContext(UserContext);
  const [writeAccess, setWriteAccess] = useState(false);

  const locationValid = useMemo(() => {
    return isValidTZLocationOfPort(props.port);
  }, [props?.port?.latitude, props?.port?.longitude]);

  useEffect(() => {
    let lWriteAccess = userCtx.user.writeAccess || props.canwrite === true;
    setWriteAccess(lWriteAccess);
  }, [userCtx.user]);

  useEffect(() => {
    let cancelled = false;

    const fetchTimezone = async () => {
      const lTimezone = await getTimezone(props?.port?.latitude, props?.port?.longitude);
      // If the port passed in changes quickly, we only wat to update with the timezone of the most recent port passed in
      if (!cancelled) {
        setTimezone(lTimezone);
      }
    };
    fetchTimezone();

    return () => {
      cancelled = true;
    };
  }, [props?.port?.latitude, props?.port?.longitude]);

  // Initialise local state when datetime or timezone changes  
  useEffect(() => {
    // sometimes we don't pass in a port, but we still need to update the state
    if (!inited && props.datetime && ((timezone && locationValid) || !props.port)) { 
      setInited(true);
      const dateTimeInTimezone = locationValid ? moment(props.datetime).tz(timezone) : moment(props.datetime);
      setLocalDate(dateTimeInTimezone.toDate());

      if (props.showTime) {
        setLocalHour(dateTimeInTimezone.format("HH"));
        setLocalMinute(dateTimeInTimezone.format("mm"));
      }
    }
  }, [props.datetime, props.showTime, timezone, locationValid, inited]);

  // Create a js Date based on the values entred in the input fields
  // Ony check for empty hours and minues if props.showTime is true
  const buildLocalDateTime = (aDate, aHours, aMinutes) => {
    if (!StringIsNullUndefinedOrEmpty(aDate) &&
      (
        props.showTime == false ||
        (
          !StringIsNullUndefinedOrEmpty(aHours) &&
          !StringIsNullUndefinedOrEmpty(aMinutes)
        )
      )
    ) {
      const updatedDate = new Date(aDate.getTime());
      updatedDate.setHours(Number(aHours));
      updatedDate.setMinutes(Number(aMinutes));
      updatedDate.setSeconds(0);
      updatedDate.setMilliseconds(0);
      return updatedDate;
    }
    else {
      return null;
    }
  };

  const handleDateTimeChange = (aDate, aHours, aMinutes) => {
    const updatedLocalDateTime = buildLocalDateTime(aDate, aHours, aMinutes);
    if (updatedLocalDateTime) {
      // If no port/location is passed in locationValid is false so we just send back what they typed in
      const value = locationValid ? zonedTimeToUtc(updatedLocalDateTime, timezone) : updatedLocalDateTime;
      props?.onChange?.(value);
    }
    else {
      // Missing date, hours or minutes input
      // Pass null to the parent on change
      // The 'init' guard in this component allows the validation to fail in the parent but the internal state of the component
      // is not overriden by the parent passing null back as the datetime prop
      props?.onChange?.(null);
    }
  };

  // UTC Date time equivalent of the entred local date time
  const utcDateTime = useMemo(() => {
    const currentLocalDateTime = buildLocalDateTime(localDate, localHour, localMinute);

    if (currentLocalDateTime) {
      const utcDate = locationValid ? zonedTimeToUtc(currentLocalDateTime, timezone) : currentLocalDateTime;
      return moment(utcDate).utc().format("DD/MM/YYYY HH:mm");
    }
    else {
      return null;
    }
  }, [localDate, localHour, localMinute, timezone, locationValid]);


  const handleIconClick = () => {
    if (datepickerRef.current) {
      datepickerRef.current.setOpen(true);
    }
  };

  return (
    <Stack>
      <div>
        <Stack style={{ display: "inline-block", marginRight: `${props.showTime !== false ? "30px" : "0px"}` }}>
          <div className="darkblue field-label mb-1">{props.showHeadLabel && <FormLabel>Date</FormLabel>}</div>
          {
            writeAccess === true ?
              <InputGroup className="flex-nowrap">
                <DatePicker
                  ref={datepickerRef}
                  disabled={!writeAccess}
                  dateFormat="dd/MM/yyyy"
                  selected={localDate}
                  todayButton="Today"
                  onChange={(newDate) => {
                    setLocalDate(newDate);
                    handleDateTimeChange(newDate, localHour, localMinute);
                  }}

                  className={`form-control datepicker-input text-start inputGroupWithAppend ${"isValid" in props && props.isValid === false ? "redBorder" : ""}`}
                  allowInput
                />
                <InputGroup.Text
                  style={{ marginLeft: "0px" }}
                  className={` inputGroupAppend datepicker-input-nested-label ${"isValid" in props && props.isValid === false ? "redBorder" : ""}  ${props.backgroundClass ? props.backgroundClass : ""}`}
                  onClick={handleIconClick}>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </InputGroup.Text>
              </InputGroup>
              :
              <div className="text-13-400 secondary-grey">{(localDate && isValid(new Date(localDate))) ? format(new Date(localDate), 'dd/MM/yyyy') : ''}</div>
          }
        </Stack>

        {
          props.showTime !== false || props.isValid === false ? (
            <Stack style={{ display: "inline-block" }}>
              <div className="darkblue field-label mb-1">{props.showHeadLabel && <FormLabel>Local Time</FormLabel>}</div>
              <Stack direction="horizontal" gap={3}>
                {props.showTime !== false && (
                  <>
                    <Form.Control
                      disabled={!writeAccess}
                      style={{ textAlign: "center" }}
                      className={
                        "isValid" in props && props.isValid === false
                          ? "redBorder input-hour form-control input secondary-grey"
                          : "input-hour form-control input secondary-grey"
                      }
                      value={localHour}
                      onBlur={() =>
                        localHour?.length === 1 ?
                          setLocalHour("0" + localHour)
                          : localHour?.length > 2 ?
                            setLocalHour(localHour.substring(localHour.length - 2, localHour.length))
                            : undefined
                      }
                      onChange={(e) => {
                        parseNumericInput(e, new HourValidationRules());
                        const hoursInput = e.target.value;
                        setLocalHour(hoursInput);
                        handleDateTimeChange(localDate, hoursInput, localMinute);
                      }}
                    />

                    <span className="time-sparater input">:</span>
                    <Form.Control
                      disabled={!writeAccess}
                      style={{ textAlign: "center" }}
                      className={
                        "isValid" in props && props.isValid === false
                          ? "redBorder input-min form-control input secondary-grey"
                          : "input-min form-control input secondary-grey"
                      }
                      value={localMinute}
                      onBlur={() =>
                        localMinute?.length === 1
                          ? setLocalMinute("0" + localMinute)
                          : localMinute?.length > 2
                            ? setLocalMinute(localMinute.substring(localMinute.length - 2, localMinute.length))
                            : undefined
                      }
                      onChange={(e) => {
                        parseNumericInput(e, new MinuteValidationRules());
                        const minutesInput = e.target.value;
                        setLocalMinute(minutesInput);
                        handleDateTimeChange(localDate, localHour, minutesInput);
                      }}
                    />
                    {props.endLabel && <span className="field-label">{props.endLabel}</span>}
                  </>
                )}

                {props.isValid === false && props.showInvalidIcon !== false ? (
                  <span className="ms-1">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      color="#dc3545"
                      data-tooltip-id="dateTooltip"
                      data-tooltip-content={props.errorMessage ? props.errorMessage : ""}
                    />
                  </span>
                ) : null}
              </Stack>
            </Stack>
          ) : null
        }
      </div >

      {!StringIsNullUndefinedOrEmpty(utcDateTime) && props.showUTCTime !== false ? <span className="mt-1 darkblue field-label">({utcDateTime} UTC) </span> : null}

      {
        props.errorMessage ? (
          <div className="validation-error-tooltip">
            <Tooltip id="dateTooltip" variant="error" />
          </div>
        ) : null
      }
    </Stack >
  );
};

export default DateTimePicker;
